import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { sidebarStore } from '@/stores/SidebarStore';

interface AppLauncherProps {
  onClose: () => void;
}

export function AppLauncher({ onClose }: AppLauncherProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleBrowse = () => {
    navigate('/marketplace');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-md mx-4 bg-card rounded-lg border border-border shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('layout.launcher.title')}
      >
        {/* Search */}
        <div className="px-4 pt-4 pb-2">
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('layout.launcher.search')}
            className="w-full h-10 px-3 bg-muted rounded-md text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Pinned */}
        {sidebarStore.pins.length > 0 && (
          <div className="px-4 py-2">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              {t('layout.launcher.pinned')}
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {sidebarStore.pins.map((pin) => (
                <button
                  key={pin.id}
                  onClick={() => { navigate(pin.href); onClose(); }}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-accent transition-colors"
                >
                  <span className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                    {pin.name.charAt(0)}
                  </span>
                  <span className="text-xs text-foreground truncate w-full text-center">{pin.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state / Browse */}
        <div className="px-4 py-3 border-t border-border">
          {sidebarStore.pins.length === 0 && (
            <p className="text-sm text-muted-foreground mb-3">{t('layout.launcher.empty')}</p>
          )}
          <button
            onClick={handleBrowse}
            className="w-full text-sm text-primary hover:underline text-center py-1"
          >
            {t('layout.launcher.browse')}
          </button>
        </div>
      </div>
    </div>
  );
}
